import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { MDBTooltip } from "mdb-react-ui-kit";

function resolveHeaderLabelText(children, titleProp) {
  if (titleProp) return titleProp;
  if (typeof children === "string") return children;
  return undefined;
}

function isTruncatedEl(el) {
  if (!el) return false;
  return el.scrollWidth > el.clientWidth + 1;
}

function useTruncation(text) {
  const ref = useRef(null);
  const truncatedRef = useRef(false);
  const [truncated, setTruncated] = useState(false);

  const check = useCallback(() => {
    if (!text) {
      truncatedRef.current = false;
      setTruncated((prev) => (prev ? false : prev));
      return;
    }
    const next = isTruncatedEl(ref.current);
    truncatedRef.current = next;
    setTruncated((prev) => (prev === next ? prev : next));
  }, [text]);

  useLayoutEffect(() => {
    check();
    const raf = requestAnimationFrame(() => requestAnimationFrame(check));
    window.addEventListener("resize", check);

    const el = ref.current;
    let observer;
    if (el && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(check);
      observer.observe(el);
      if (el.parentElement) observer.observe(el.parentElement);
      const th = el.closest("th");
      if (th) observer.observe(th);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", check);
      observer?.disconnect();
    };
  }, [check, text]);

  return { ref, truncated, truncatedRef };
}

/**
 * Header label — … when tight; products MDBTooltip only when truncated.
 * Tooltip stays mounted (stable DOM / same UI); onOpen is blocked when not truncated.
 */
export function TableHeaderLabel({
  children,
  className = "",
  title: titleProp,
}) {
  const text = resolveHeaderLabelText(children, titleProp);
  const { ref, truncatedRef } = useTruncation(text);

  const label = (
    <span
      ref={ref}
      className={`oh-table-header-ellipsis${className ? ` ${className}` : ""}`}
    >
      {children}
    </span>
  );

  if (!text) {
    return <span className="oh-table-header-tip-wrap">{label}</span>;
  }

  return (
    <MDBTooltip
      tag="span"
      title={text}
      placement="top"
      wrapperClass="oh-table-header-tip-wrap"
      onOpen={(e) => {
        if (!truncatedRef.current) e.preventDefault();
      }}
    >
      {label}
    </MDBTooltip>
  );
}

/**
 * Cell text — … when truncated; products MDBTooltip only when truncated.
 */
export function TableCellEllipsis({ children, title, className = "" }) {
  const label = title ?? (typeof children === "string" ? children : undefined);
  const text =
    label != null && String(label).trim() !== "" ? String(label) : undefined;
  const { ref, truncatedRef } = useTruncation(text);

  const inner = (
    <span
      ref={ref}
      className={`oh-table-cell-ellipsis oh-table-cell-ellipsis--truncate${
        className ? ` ${className}` : ""
      }`}
    >
      {children}
    </span>
  );

  if (!text) {
    return <span className="oh-table-cell-ellipsis-wrap">{inner}</span>;
  }

  return (
    <span className="oh-table-cell-ellipsis-wrap">
      <MDBTooltip
        tag="span"
        title={text}
        placement="top"
        onOpen={(e) => {
          if (!truncatedRef.current) e.preventDefault();
        }}
      >
        {inner}
      </MDBTooltip>
    </span>
  );
}

/** Two-line stack (primary + muted), each line wraps when needed. */
export function TableCellStack({ primary, secondary, primaryTitle, secondaryTitle }) {
  return (
    <div className="oh-table-cell-stack">
      <span
        className="oh-table-cell-stack__line oh-table-cell-stack__line--primary"
        title={primaryTitle ?? primary}
      >
        {primary}
      </span>
      {secondary != null && secondary !== "" ? (
        <span
          className="oh-table-cell-stack__line oh-table-cell-stack__line--muted"
          title={secondaryTitle ?? secondary}
        >
          {secondary}
        </span>
      ) : null}
    </div>
  );
}

/** Inline row (e.g. ID + icon) — text truncates, trailing controls stay visible. */
export function TableCellInline({ text, title, trailing }) {
  return (
    <div className="oh-table-cell-inline" onClick={(e) => e.stopPropagation()}>
      <span className="oh-table-cell-inline__text" title={title ?? text}>
        {text}
      </span>
      {trailing ? <span className="oh-table-cell-inline__trailing">{trailing}</span> : null}
    </div>
  );
}
